import * as React from "react";
import { Box, Drawer, useMediaQuery } from "@mui/material";
import { Outlet, useLocation } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import SideBar from "./SideBar";
import Header from "./Header";
import API from "../Api/api";
import { colors, SIDEBAR_WIDTH } from "../brand";

export default function AppLayout() {
  // Below this width the menu slides over the page instead of pushing it
  const isSmall = useMediaQuery("(max-width:1100px)", { noSsr: true });
  const [open, setOpen] = React.useState(!isSmall);
  const { pathname } = useLocation();

  React.useEffect(() => {
    setOpen(!isSmall);
  }, [isSmall]);

  const reconRole = React.useMemo(() => {
    try {
      return JSON.parse(secureLocalStorage.getItem("profile"))?.results
        ?.reconciliation;
    } catch {
      return "";
    }
  }, []);
  const canApprove = reconRole === "Approver" || reconRole === "Administrator";

  // Reports waiting for approval, shown on the bell and the Approvals menu.
  // Checked again on every page change so it drops after approving.
  const [pendingCount, setPendingCount] = React.useState(0);
  React.useEffect(() => {
    if (!canApprove) return undefined;
    let mounted = true;
    API.get(`/api/v1/reconciliation-submission?approved=false`)
      .then((res) => {
        if (mounted) setPendingCount(Array.isArray(res.data) ? res.data.length : 0);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [canApprove, pathname]);

  // Deletion requests waiting for an Administrator's decision
  const isAdmin = reconRole === "Administrator";
  const [deletionCount, setDeletionCount] = React.useState(0);
  React.useEffect(() => {
    if (!isAdmin) return undefined;
    let mounted = true;
    API.get(`/api/v1/reconciliation-deletion-requests?status=pending`)
      .then((res) => {
        if (mounted) setDeletionCount(res.data?.results?.length || 0);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [isAdmin, pathname]);

  const sidebar = (
    <SideBar
      pendingCount={pendingCount}
      deletionCount={deletionCount}
      onNavigate={isSmall ? () => setOpen(false) : undefined}
    />
  );

  return (
    <Box sx={{ display: "flex", height: "100dvh", backgroundColor: colors.background }}>
      {isSmall ? (
        <Drawer
          open={open}
          onClose={() => setOpen(false)}
          ModalProps={{ keepMounted: true }}
          PaperProps={{ sx: { border: "none" } }}
        >
          {sidebar}
        </Drawer>
      ) : (
        <Box
          sx={{
            width: open ? SIDEBAR_WIDTH : 0,
            flexShrink: 0,
            overflow: "hidden",
            transition: "width 0.25s ease",
          }}
        >
          {sidebar}
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Header
          sidebarOpen={open}
          onToggleSidebar={() => setOpen((v) => !v)}
          pendingCount={pendingCount}
          deletionCount={deletionCount}
          canApprove={canApprove}
        />
        <Box
          component="main"
          id="main-content"
          sx={{
            flex: 1,
            overflow: "auto",
            p: { xs: 1.5, sm: 2, lg: 3 },
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
